<template>
    <div class="content-wrapper">
        <app-breadcrumb :page-title="$t('global_reporting')" :icon="'bar-chart-2'" />

        <div class="global-report">
            <div class="card global-report__filters">
                <div class="global-report__filters-header">
                    <div class="global-report__filters-copy">
                        <span class="global-report__eyebrow">{{ $t('decision_support_overview') }}</span>
                        <h5 class="mb-0">{{ $t('global_reporting') }}</h5>
                        <p class="global-report__filters-description">
                            {{ $t('global_reporting_intro') }}
                        </p>
                    </div>

                    <div class="global-report__filters-period">
                        <small>{{ $t('reporting_period') }}</small>
                        <strong>{{ reportPeriodLabel }}</strong>
                    </div>
                </div>

                <div class="global-report__filters-grid">
                    <div class="form-group">
                        <label>{{ $t('date_from') }}</label>
                        <app-input
                            id="global-report-date-from"
                            v-model="filters.date_from"
                            type="date"
                        />
                    </div>

                    <div class="form-group">
                        <label>{{ $t('date_to') }}</label>
                        <app-input
                            id="global-report-date-to"
                            v-model="filters.date_to"
                            type="date"
                        />
                    </div>

                    <div class="form-group">
                        <label>{{ $t('site') }}</label>
                        <app-input
                            id="global-report-site"
                            v-model="filters.site_id"
                            type="search-select"
                            :list="siteOptions"
                            list-value-field="value"
                            list-class="global-report__select-menu"
                        />
                    </div>

                    <div class="form-group">
                        <label>{{ $t('destination') }}</label>
                        <app-input
                            id="global-report-destination"
                            v-model="filters.destination_id"
                            type="search-select"
                            :list="destinationOptions"
                            list-value-field="value"
                            list-class="global-report__select-menu"
                        />
                    </div>

                    <div class="form-group">
                        <label>{{ $t('transporter') }}</label>
                        <app-input
                            id="global-report-transporter"
                            v-model="filters.transporter_id"
                            type="search-select"
                            :list="transporterOptions"
                            list-value-field="value"
                            list-class="global-report__select-menu"
                        />
                    </div>

                    <div class="global-report__filter-actions">
                        <button
                            type="button"
                            class="btn btn-primary"
                            :disabled="isLoading"
                            @click.prevent="fetchReport"
                        >
                            {{ $t('apply_filters') }}
                        </button>

                        <button
                            type="button"
                            class="btn btn-light"
                            :disabled="isLoading"
                            @click.prevent="resetFilters"
                        >
                            {{ $t('clear_filters') }}
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="isLoading && !report" class="global-report__state">
                <app-overlay-loader />
            </div>

            <div v-else-if="report" class="global-report__content position-relative" :class="{ 'loading-opacity': isLoading }">
                <app-overlay-loader v-if="isLoading" />

                <section class="global-report__hero">
                    <div class="global-report__hero-copy">
                        <span class="global-report__eyebrow">{{ $t('decision_support_overview') }}</span>
                        <h4>{{ $t('global_reporting') }}</h4>
                        <p>
                            {{ $t('global_reporting_intro') }}
                        </p>

                        <div class="global-report__hero-grid">
                            <article
                                v-for="item in spotlightCards"
                                :key="item.key"
                                class="global-report__hero-card"
                            >
                                <span>{{ item.label }}</span>
                                <strong>{{ item.value }}</strong>
                                <small>{{ item.meta }}</small>
                            </article>
                        </div>
                    </div>

                    <div class="global-report__hero-aside">
                        <div class="global-report__score-shell">
                            <div class="global-report__score-ring" :style="readinessRingStyle">
                                <div class="global-report__score-core">
                                    <strong>{{ readinessScore }}</strong>
                                    <span>/100</span>
                                </div>
                            </div>

                            <div class="global-report__score-copy">
                                <span class="global-report__eyebrow">{{ $t('readiness_index') }}</span>
                                <strong>{{ readinessScore }}/100</strong>
                                <p>{{ reportPeriodLabel }}</p>
                            </div>
                        </div>

                        <div class="global-report__signal-grid">
                            <article class="global-report__signal-card">
                                <span>{{ $t('response_tempo') }}</span>
                                <strong>{{ formatMinutes(report.summary.average_acknowledge_minutes) }}</strong>
                                <div class="global-report__signal-bar">
                                    <span :style="{ width: `${responseTempoScore}%` }"></span>
                                </div>
                            </article>

                            <article class="global-report__signal-card">
                                <span>{{ $t('incident_pressure') }}</span>
                                <strong>{{ formatPercentage(incidentPressureScore) }}</strong>
                                <div class="global-report__signal-bar global-report__signal-bar--alert">
                                    <span :style="{ width: `${incidentPressureScore}%` }"></span>
                                </div>
                            </article>

                            <article class="global-report__signal-card">
                                <span>{{ $t('tracker_coverage') }}</span>
                                <strong>{{ formatPercentage(report.summary.tracker_coverage) }}</strong>
                                <div class="global-report__signal-bar global-report__signal-bar--cool">
                                    <span :style="{ width: `${trackingCoverageScore}%` }"></span>
                                </div>
                            </article>
                        </div>
                    </div>
                </section>

                <div class="row">
                    <div
                        v-for="card in summaryCards"
                        :key="card.key"
                        class="col-12 col-sm-6 col-xl-3 mb-primary"
                    >
                        <article class="global-report__metric-card" :style="{ '--metric-accent': card.accent }">
                            <div class="global-report__metric-icon">
                                <app-icon :name="card.icon" class="size-18" />
                            </div>
                            <div class="global-report__metric-copy">
                                <span>{{ card.label }}</span>
                                <strong>{{ card.value }}</strong>
                            </div>
                        </article>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12 mb-primary">
                        <section class="global-report__panel global-report__panel--briefing">
                            <div class="global-report__panel-head">
                                <div>
                                    <span class="global-report__eyebrow">{{ $t('incident_breakdown') }}</span>
                                    <h6>{{ $t('strategic_focus') }}</h6>
                                </div>
                            </div>

                            <div class="global-report__briefing">
                                <div class="global-report__briefing-block">
                                    <span>{{ $t('incident_watchlist') }}</span>
                                    <div v-if="incidentTypeHighlights.length" class="global-report__leaderboard">
                                        <article
                                            v-for="item in incidentTypeHighlights"
                                            :key="item.key"
                                            class="global-report__leader"
                                        >
                                            <div class="global-report__leader-top">
                                                <strong>{{ item.label }}</strong>
                                                <span>{{ item.valueLabel }}</span>
                                            </div>
                                            <small>{{ item.meta }}</small>
                                            <div class="global-report__leader-meter">
                                                <span :style="{ width: getMeterWidth(item.share), background: item.color }"></span>
                                            </div>
                                        </article>
                                    </div>
                                    <div v-else class="global-report__empty">{{ $t('not_found') }}</div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12 col-lg-6 mb-primary">
                        <section class="global-report__panel global-report__panel--chart">
                            <div class="global-report__panel-head">
                                <div>
                                    <span class="global-report__eyebrow">{{ $t('incident_breakdown') }}</span>
                                    <h6>{{ $t('incidents_by_severity') }}</h6>
                                </div>
                            </div>

                            <div v-if="hasChartData(severityChart)" class="global-report__chart-stage">
                                <app-chart
                                    :key="`severity-chart-${chartRenderSeed}`"
                                    type="horizontal-line-chart"
                                    :height="severityChart.height"
                                    :labels="severityChart.labels"
                                    :data-sets="severityChart.dataSets"
                                    :without-decimal-point="true"
                                />
                            </div>
                            <div v-else class="global-report__empty">{{ $t('not_found') }}</div>
                        </section>
                    </div>

                    <div class="col-12 col-lg-6 mb-primary">
                        <section class="global-report__panel global-report__panel--chart">
                            <div class="global-report__panel-head">
                                <div>
                                    <span class="global-report__eyebrow">{{ $t('incident_breakdown') }}</span>
                                    <h6>{{ $t('incidents_by_category') }}</h6>
                                </div>
                            </div>

                            <div v-if="hasChartData(categoryChart)" class="global-report__chart-stage">
                                <app-chart
                                    :key="`category-chart-${chartRenderSeed}`"
                                    type="horizontal-line-chart"
                                    :height="categoryChart.height"
                                    :labels="categoryChart.labels"
                                    :data-sets="categoryChart.dataSets"
                                    :without-decimal-point="true"
                                />
                            </div>
                            <div v-else class="global-report__empty">{{ $t('not_found') }}</div>
                        </section>
                    </div>

                    <div class="col-12 col-lg-6 mb-primary">
                        <section class="global-report__panel global-report__panel--chart">
                            <div class="global-report__panel-head">
                                <div>
                                    <span class="global-report__eyebrow">{{ $t('operations_breakdown') }}</span>
                                    <h6>{{ $t('corridors_overview') }}</h6>
                                </div>
                            </div>

                            <div v-if="hasChartData(corridorChart)" class="global-report__chart-stage">
                                <app-chart
                                    :key="`corridor-chart-${chartRenderSeed}`"
                                    type="horizontal-line-chart"
                                    :height="corridorChart.height"
                                    :labels="corridorChart.labels"
                                    :data-sets="corridorChart.dataSets"
                                    :without-decimal-point="true"
                                />
                            </div>
                            <div v-else class="global-report__empty">{{ $t('not_found') }}</div>

                            <div v-if="corridorHighlights.length" class="global-report__leaderboard global-report__leaderboard--compact">
                                <article
                                    v-for="item in corridorHighlights"
                                    :key="item.key"
                                    class="global-report__leader"
                                >
                                    <div class="global-report__leader-top">
                                        <strong>{{ item.label }}</strong>
                                        <span>{{ item.valueLabel }}</span>
                                    </div>
                                    <small>{{ item.meta }}</small>
                                    <div class="global-report__leader-meter">
                                        <span :style="{ width: getMeterWidth(item.share), background: item.color }"></span>
                                    </div>
                                </article>
                            </div>
                        </section>
                    </div>

                    <div class="col-12 col-lg-6 mb-primary">
                        <section class="global-report__panel global-report__panel--chart">
                            <div class="global-report__panel-head">
                                <div>
                                    <span class="global-report__eyebrow">{{ $t('operations_breakdown') }}</span>
                                    <h6>{{ $t('transporter_activity') }}</h6>
                                </div>
                            </div>

                            <div v-if="hasChartData(transporterChart)" class="global-report__chart-stage">
                                <app-chart
                                    :key="`transporter-chart-${chartRenderSeed}`"
                                    type="horizontal-line-chart"
                                    :height="transporterChart.height"
                                    :labels="transporterChart.labels"
                                    :data-sets="transporterChart.dataSets"
                                />
                            </div>
                            <div v-else class="global-report__empty">{{ $t('not_found') }}</div>

                            <div v-if="transporterHighlights.length" class="global-report__leaderboard global-report__leaderboard--compact">
                                <article
                                    v-for="item in transporterHighlights"
                                    :key="item.key"
                                    class="global-report__leader"
                                >
                                    <div class="global-report__leader-top">
                                        <strong>{{ item.label }}</strong>
                                        <span>{{ item.valueLabel }}</span>
                                    </div>
                                    <small>{{ item.meta }}</small>
                                    <div class="global-report__leader-meter">
                                        <span :style="{ width: getMeterWidth(item.share), background: item.color }"></span>
                                    </div>
                                </article>
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            <div v-else class="global-report__empty global-report__empty--full">
                {{ $t('failed_response') }}
            </div>
        </div>
    </div>
</template>

<script>
import { FormMixin } from '../../../../core/mixins/form/FormMixin';
import { GLOBAL_REPORT } from '../../../Config/ApiUrl';
import { getDecimalSeparator, numberFormatter } from '../../../Helpers/Helpers';

export default {
    name: 'GlobalReport',
    mixins: [FormMixin],
    data() {
        return {
            isLoading: false,
            report: null,
            chartRenderSeed: 0,
            filterOptions: {
                sites: [],
                destinations: [],
                transporters: [],
            },
            filters: {
                date_from: this.getYearStart(),
                date_to: this.getToday(),
                site_id: '',
                destination_id: '',
                transporter_id: '',
            },
            chartPalette: [
                '#0f766e',
                '#0891b2',
                '#0ea5e9',
                '#f97316',
                '#f59e0b',
                '#7c3aed',
                '#ef4444',
                '#10b981',
            ],
        };
    },
    computed: {
        siteOptions() {
            return [
                { id: '', value: this.$t('all_sites') },
                ...this.filterOptions.sites,
            ];
        },
        destinationOptions() {
            return [
                { id: '', value: this.$t('all_destinations') },
                ...this.filterOptions.destinations,
            ];
        },
        transporterOptions() {
            return [
                { id: '', value: this.$t('all_transporters') },
                ...this.filterOptions.transporters,
            ];
        },
        reportPeriodLabel() {
            return `${this.filters.date_from} → ${this.filters.date_to}`;
        },
        summaryCards() {
            if (!this.report) {
                return [];
            }

            return [
                {
                    key: 'trips_monitored',
                    label: this.$t('trips_monitored'),
                    value: this.formatCount(this.report.summary.trips_monitored),
                    icon: 'truck',
                    accent: '#0f766e',
                },
                {
                    key: 'bundles_moved',
                    label: this.$t('bundles_moved'),
                    value: this.formatCount(this.report.summary.bundles_moved),
                    icon: 'package',
                    accent: '#0891b2',
                },
                {
                    key: 'net_weight_moved',
                    label: this.$t('net_weight_moved'),
                    value: this.formatWeight(this.report.summary.net_weight_moved),
                    icon: 'archive',
                    accent: '#f97316',
                },
                {
                    key: 'open_incidents',
                    label: this.$t('open_incidents'),
                    value: this.formatCount(this.report.summary.open_incidents),
                    icon: 'alert-octagon',
                    accent: '#ef4444',
                },
                {
                    key: 'critical_incidents',
                    label: this.$t('critical_incidents'),
                    value: this.formatCount(this.report.summary.critical_incidents),
                    icon: 'alert-triangle',
                    accent: '#b91c1c',
                },
                {
                    key: 'tracker_coverage',
                    label: this.$t('tracker_coverage'),
                    value: this.formatPercentage(this.report.summary.tracker_coverage),
                    icon: 'radio',
                    accent: '#0ea5e9',
                },
                {
                    key: 'average_delay_minutes',
                    label: this.$t('average_delay'),
                    value: this.formatMinutes(this.report.summary.average_delay_minutes),
                    icon: 'clock',
                    accent: '#f59e0b',
                },
                {
                    key: 'average_acknowledge_minutes',
                    label: this.$t('average_acknowledge'),
                    value: this.formatMinutes(this.report.summary.average_acknowledge_minutes),
                    icon: 'check-circle',
                    accent: '#7c3aed',
                },
            ];
        },
        readinessScore() {
            if (!this.report) {
                return 0;
            }

            const trackerCoverage = this.clampScore(this.report.summary.tracker_coverage);
            const tripsMonitored = Number(this.report.summary.trips_monitored || 0);
            const openIncidents = Number(this.report.summary.open_incidents || 0);
            const averageDelay = Number(this.report.summary.average_delay_minutes || 0);
            const averageAcknowledge = Number(this.report.summary.average_acknowledge_minutes || 0);
            const incidentPenalty = tripsMonitored > 0
                ? this.clampScore((openIncidents / tripsMonitored) * 100)
                : 0;

            const score = (
                trackerCoverage * 0.5
                + this.normalizeInverseScore(averageDelay, 180) * 0.2
                + this.normalizeInverseScore(averageAcknowledge, 120) * 0.2
                + (100 - incidentPenalty) * 0.1
            );

            return this.clampScore(score);
        },
        readinessRingStyle() {
            return {
                background: `conic-gradient(${this.readinessColor} 0 ${this.readinessScore}%, rgba(15, 23, 42, 0.08) ${this.readinessScore}% 100%)`,
            };
        },
        readinessColor() {
            if (this.readinessScore >= 75) {
                return '#0f766e';
            }

            if (this.readinessScore >= 50) {
                return '#f59e0b';
            }

            return '#ef4444';
        },
        responseTempoScore() {
            if (!this.report) {
                return 0;
            }

            return this.normalizeInverseScore(this.report.summary.average_acknowledge_minutes, 120);
        },
        incidentPressureScore() {
            if (!this.report) {
                return 0;
            }

            const tripsMonitored = Number(this.report.summary.trips_monitored || 0);
            const openIncidents = Number(this.report.summary.open_incidents || 0);
            const criticalIncidents = Number(this.report.summary.critical_incidents || 0);

            if (!tripsMonitored) {
                return 0;
            }

            return this.clampScore(((openIncidents + (criticalIncidents * 2)) / tripsMonitored) * 100);
        },
        trackingCoverageScore() {
            if (!this.report) {
                return 0;
            }

            return this.clampScore(this.report.summary.tracker_coverage);
        },
        spotlightCards() {
            if (!this.report) {
                return [];
            }

            const leadingCorridor = this.report.operations_breakdown.corridors[0];
            const leadingTransporter = this.report.operations_breakdown.transporters[0];
            const dominantSeverity = this.report.incident_breakdown.by_severity[0];
            const dominantType = this.report.incident_breakdown.by_type[0];

            return [
                {
                    key: 'leading-corridor',
                    label: this.$t('route'),
                    value: leadingCorridor ? this.routeLabel(leadingCorridor) : '---',
                    meta: leadingCorridor
                        ? `${this.formatCount(leadingCorridor.trips)} ${this.$t('trips')} · ${this.formatWeight(leadingCorridor.net_weight)}`
                        : this.$t('not_found'),
                },
                {
                    key: 'leading-transporter',
                    label: this.$t('transporter'),
                    value: leadingTransporter ? leadingTransporter.label : '---',
                    meta: leadingTransporter
                        ? `${this.formatCount(leadingTransporter.trips)} ${this.$t('trips')} · ${this.formatWeight(leadingTransporter.net_weight)}`
                        : this.$t('not_found'),
                },
                {
                    key: 'dominant-severity',
                    label: this.$t('severity'),
                    value: dominantSeverity ? this.translateLabel(dominantSeverity.key) : '---',
                    meta: dominantSeverity
                        ? `${this.formatCount(dominantSeverity.total)} ${this.$t('count')}`
                        : this.$t('not_found'),
                },
                {
                    key: 'dominant-type',
                    label: this.$t('incident_type'),
                    value: dominantType ? this.translateLabel(dominantType.key) : '---',
                    meta: dominantType
                        ? `${this.formatCount(dominantType.total)} ${this.$t('count')}`
                        : this.$t('not_found'),
                },
            ];
        },
        severityChart() {
            if (!this.report) {
                return this.emptyChartData(280);
            }

            return this.buildChartData(this.report.incident_breakdown.by_severity, {
                labelGetter: row => this.translateLabel(row.key),
                valueGetter: row => row.total,
                datasetLabel: this.$t('count'),
                barThickness: 18,
                height: 280,
                withoutDecimalPoint: true,
            });
        },
        categoryChart() {
            if (!this.report) {
                return this.emptyChartData(320);
            }

            return this.buildChartData(this.report.incident_breakdown.by_category.slice(0, 6), {
                labelGetter: row => this.translateLabel(row.key),
                valueGetter: row => row.total,
                datasetLabel: this.$t('count'),
                barThickness: 16,
                height: 320,
                withoutDecimalPoint: true,
            });
        },
        corridorChart() {
            if (!this.report) {
                return this.emptyChartData(320);
            }

            return this.buildChartData(this.report.operations_breakdown.corridors.slice(0, 6), {
                labelGetter: row => this.routeLabel(row),
                valueGetter: row => row.trips,
                datasetLabel: this.$t('trips'),
                barThickness: 16,
                height: 320,
                withoutDecimalPoint: true,
            });
        },
        transporterChart() {
            if (!this.report) {
                return this.emptyChartData(320);
            }

            return this.buildChartData(this.report.operations_breakdown.transporters.slice(0, 6), {
                labelGetter: row => row.label || '---',
                valueGetter: row => row.net_weight,
                datasetLabel: this.$t('net_weight'),
                barThickness: 16,
                height: 320,
            });
        },
        incidentTypeHighlights() {
            if (!this.report) {
                return [];
            }

            return this.buildLeaderboard(this.report.incident_breakdown.by_type, {
                valueGetter: row => row.total,
                labelGetter: row => this.translateLabel(row.key),
                valueFormatter: value => this.formatCount(value),
                metaFormatter: (row, share) => this.formatPercentage(share),
                limit: 5,
            });
        },
        corridorHighlights() {
            if (!this.report) {
                return [];
            }

            return this.buildLeaderboard(this.report.operations_breakdown.corridors, {
                valueGetter: row => row.trips,
                labelGetter: row => this.routeLabel(row),
                valueFormatter: value => `${this.formatCount(value)} ${this.$t('trips')}`,
                metaFormatter: (row, share) => `${this.formatWeight(row.net_weight)} · ${this.formatPercentage(share)} ${this.$t('share_of_visible_trips')}`,
                limit: 4,
            });
        },
        transporterHighlights() {
            if (!this.report) {
                return [];
            }

            return this.buildLeaderboard(this.report.operations_breakdown.transporters, {
                valueGetter: row => row.net_weight,
                labelGetter: row => row.label || '---',
                valueFormatter: (value) => this.formatWeight(value),
                metaFormatter: (row, share) => `${this.formatCount(row.trips)} ${this.$t('trips')} · ${this.formatPercentage(share)} ${this.$t('share_of_visible_weight')}`,
                limit: 4,
            });
        },
    },
    created() {
        this.fetchReport();
    },
    methods: {
        fetchReport() {
            this.isLoading = true;

            this.axiosGet(GLOBAL_REPORT, {
                params: {
                    ...this.filters,
                },
            }).then(({ data }) => {
                this.report = data;
                this.filterOptions = data.filter_options || this.filterOptions;
                this.filters = {
                    ...this.filters,
                    ...(data.filters || {}),
                };
                this.chartRenderSeed += 1;
            }).catch(() => {
                this.report = null;
            }).finally(() => {
                this.isLoading = false;
            });
        },
        resetFilters() {
            this.filters = {
                date_from: this.getYearStart(),
                date_to: this.getToday(),
                site_id: '',
                destination_id: '',
                transporter_id: '',
            };

            this.fetchReport();
        },
        hasChartData(chart) {
            return Boolean(chart && chart.labels && chart.labels.length && chart.dataSets && chart.dataSets.length);
        },
        emptyChartData(height = 280) {
            return {
                labels: [],
                dataSets: [],
                height,
            };
        },
        buildChartData(rows, { labelGetter, valueGetter, datasetLabel, barThickness = 18, height = 280, withoutDecimalPoint = false }) {
            const items = Array.isArray(rows) ? rows : [];

            return {
                labels: items.map((row) => labelGetter(row)),
                dataSets: [
                    {
                        label: datasetLabel,
                        backgroundColor: items.map((row, index) => this.chartPalette[index % this.chartPalette.length]),
                        hoverBackgroundColor: items.map((row, index) => this.chartPalette[index % this.chartPalette.length]),
                        borderWidth: 0,
                        barThickness,
                        maxBarThickness: barThickness,
                        data: items.map((row) => Number(valueGetter(row) || 0)),
                    },
                ],
                height,
                withoutDecimalPoint,
            };
        },
        buildLeaderboard(rows, { valueGetter, labelGetter, valueFormatter, metaFormatter, limit = 5 }) {
            const items = Array.isArray(rows) ? rows.slice(0, limit) : [];
            const total = items.reduce((sum, row) => sum + Number(valueGetter(row) || 0), 0);

            return items.map((row, index) => {
                const value = Number(valueGetter(row) || 0);
                const share = total > 0 ? (value / total) * 100 : 0;

                return {
                    key: `${labelGetter(row)}-${index}`,
                    label: labelGetter(row),
                    valueLabel: valueFormatter ? valueFormatter(value, row, share) : this.formatCount(value),
                    meta: metaFormatter ? metaFormatter(row, share, value, index) : this.formatPercentage(share),
                    share,
                    color: this.chartPalette[index % this.chartPalette.length],
                };
            });
        },
        translateLabel(key) {
            if (!key) {
                return '---';
            }

            const translated = this.$t(key);

            return translated && translated !== key ? translated : key;
        },
        routeLabel(row) {
            return `${row.origin || '---'} → ${row.destination || '---'}`;
        },
        formatWeight(value) {
            return `${numberFormatter(value || 0)} ${this.$t('tonnes')}`;
        },
        formatMinutes(value) {
            return `${numberFormatter(value || 0)} ${this.$t('mins')}`;
        },
        formatPercentage(value) {
            return `${numberFormatter(value || 0)}%`;
        },
        formatCount(value) {
            const decimalSeparator = getDecimalSeparator();
            const formatted = numberFormatter(Math.round(Number(value || 0))).toString();

            return decimalSeparator && formatted.includes(decimalSeparator)
                ? formatted.split(decimalSeparator)[0]
                : formatted;
        },
        getMeterWidth(value) {
            if (!value) {
                return '0%';
            }

            return `${Math.max(8, Math.min(100, value))}%`;
        },
        normalizeInverseScore(value, maxValue) {
            const normalized = 100 - ((Number(value || 0) / maxValue) * 100);

            return this.clampScore(normalized);
        },
        clampScore(value) {
            return Math.max(0, Math.min(100, Math.round(Number(value || 0))));
        },
        getToday() {
            return this.toInputDate(new Date());
        },
        getYearStart() {
            const date = new Date();
            date.setMonth(0, 1);
            return this.toInputDate(date);
        },
        toInputDate(date) {
            const year = date.getFullYear();
            const month = `${date.getMonth() + 1}`.padStart(2, '0');
            const day = `${date.getDate()}`.padStart(2, '0');

            return `${year}-${month}-${day}`;
        },
    },
};
</script>

<style>
.global-report {
    display: grid;
    gap: 1.25rem;
}

.global-report__filters,
.global-report__panel,
.global-report__metric-card,
.global-report__hero {
    border: 1px solid var(--default-border-color, #dbe4f0);
    background: var(--default-bg, #fff);
    box-shadow: 0 18px 50px rgba(15, 23, 42, 0.06);
}

.global-report__filters {
    padding: 1.1rem 1.2rem 1rem;
    position: relative;
    z-index: 30;
    overflow: visible;
}

.global-report__filters-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 1.1rem;
    align-items: start;
    margin-bottom: 1.15rem;
}

.global-report__filters-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.9rem 1rem;
    align-items: end;
    overflow: visible;
}

.global-report__filters-grid .form-group {
    position: relative;
    z-index: 2;
    margin-bottom: 0;
}

.global-report__filters-grid .form-group:focus-within {
    z-index: 90;
}

.global-report__filters-grid .form-group label {
    margin-bottom: 0.45rem;
    color: var(--default-font-color, #0f172a);
    font-weight: 600;
}

.global-report__filters-copy {
    min-width: 0;
    max-width: 44rem;
}

.global-report__filters-copy h5 {
    margin-top: 0.25rem;
    font-size: 1.3rem;
    line-height: 1.2;
    color: var(--default-font-color, #0f172a);
}

.global-report__filters-description {
    margin-top: 0.45rem;
    margin-bottom: 0;
    color: var(--secondary, #64748b);
    line-height: 1.55;
    max-width: 38rem;
}

.global-report__filters-period {
    min-width: 13rem;
    padding: 0.9rem 1rem;
    border: 1px solid rgba(14, 165, 233, 0.16);
    background: linear-gradient(135deg, rgba(15, 118, 110, 0.08), rgba(249, 115, 22, 0.09));
    text-align: right;
    align-self: stretch;
}

.global-report__filters-period small {
    display: block;
    color: var(--secondary, #64748b);
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.global-report__filters-period strong {
    display: block;
    margin-top: 0.35rem;
    color: var(--default-font-color, #0f172a);
    font-size: 0.95rem;
    line-height: 1.35;
    word-break: break-word;
}

.global-report__filter-actions {
    display: flex;
    gap: 0.65rem;
    align-items: center;
    justify-content: flex-end;
    grid-column: 1 / -1;
    padding-top: 0.15rem;
}

.global-report__content {
    display: grid;
    gap: 1.25rem;
}

.global-report__hero {
    position: relative;
    overflow: hidden;
    padding: 1.3rem;
    display: grid;
    grid-template-columns: minmax(0, 1.25fr) minmax(320px, 0.9fr);
    gap: 1.2rem;
    background:
        radial-gradient(circle at top right, rgba(249, 115, 22, 0.16), transparent 36%),
        linear-gradient(135deg, rgba(15, 118, 110, 0.08), rgba(255, 255, 255, 0.94));
}

.global-report__hero::after {
    content: '';
    position: absolute;
    right: -4rem;
    bottom: -5rem;
    width: 15rem;
    height: 15rem;
    border-radius: 999px;
    background: radial-gradient(circle, rgba(14, 165, 233, 0.16), transparent 70%);
    pointer-events: none;
}

.global-report__hero-copy,
.global-report__hero-aside {
    position: relative;
    z-index: 1;
}

.global-report__hero-copy h4 {
    margin: 0.35rem 0 0.6rem;
    font-size: 1.55rem;
    color: var(--default-font-color, #0f172a);
}

.global-report__hero-copy p {
    margin: 0;
    max-width: 42rem;
    line-height: 1.65;
    color: var(--secondary, #64748b);
}

.global-report__hero-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.8rem;
    margin-top: 1rem;
}

.global-report__hero-card {
    border: 1px solid rgba(255, 255, 255, 0.55);
    background: rgba(255, 255, 255, 0.78);
    backdrop-filter: blur(8px);
    padding: 0.9rem 0.95rem;
}

.global-report__hero-card span,
.global-report__signal-card span {
    display: block;
    font-size: 0.76rem;
    font-weight: 700;
    line-height: 1.35;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--secondary, #64748b);
}

.global-report__hero-card strong,
.global-report__signal-card strong {
    display: block;
    margin-top: 0.4rem;
    font-size: 1rem;
    line-height: 1.3;
    color: var(--default-font-color, #0f172a);
}

.global-report__hero-card small,
.global-report__signal-card small {
    display: block;
    margin-top: 0.3rem;
    line-height: 1.5;
    color: var(--secondary, #64748b);
}

.global-report__hero-aside {
    display: grid;
    gap: 0.95rem;
}

.global-report__score-shell {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.82);
    border: 1px solid rgba(255, 255, 255, 0.62);
    backdrop-filter: blur(6px);
}

.global-report__score-ring {
    width: 7.5rem;
    height: 7.5rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.global-report__score-core {
    width: 5.5rem;
    height: 5.5rem;
    border-radius: 999px;
    background: #fff;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--default-font-color, #0f172a);
    box-shadow: inset 0 0 0 1px rgba(219, 228, 240, 0.9);
}

.global-report__score-core strong {
    font-size: 1.65rem;
    line-height: 1;
}

.global-report__score-core span {
    margin-top: 0.2rem;
    font-size: 0.72rem;
    color: var(--secondary, #64748b);
}

.global-report__score-copy strong {
    display: block;
    margin-top: 0.35rem;
    font-size: 1.45rem;
    color: var(--default-font-color, #0f172a);
}

.global-report__score-copy p {
    margin: 0.35rem 0 0;
    color: var(--secondary, #64748b);
    line-height: 1.55;
}

.global-report__signal-grid {
    display: grid;
    gap: 0.75rem;
}

.global-report__signal-card {
    padding: 0.9rem 1rem;
    background: rgba(255, 255, 255, 0.82);
    border: 1px solid rgba(255, 255, 255, 0.64);
}

.global-report__signal-bar {
    margin-top: 0.7rem;
    height: 0.45rem;
    border-radius: 999px;
    background: rgba(15, 118, 110, 0.12);
    overflow: hidden;
}

.global-report__signal-bar span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #0f766e, #0ea5e9);
}

.global-report__signal-bar--alert {
    background: rgba(239, 68, 68, 0.12);
}

.global-report__signal-bar--alert span {
    background: linear-gradient(90deg, #ef4444, #f97316);
}

.global-report__signal-bar--cool {
    background: rgba(14, 165, 233, 0.12);
}

.global-report__signal-bar--cool span {
    background: linear-gradient(90deg, #0ea5e9, #7c3aed);
}

.global-report__metric-card {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    padding: 1rem 1.05rem;
    min-height: 100%;
    border-top: 3px solid var(--metric-accent, var(--brand-primary, #f97316));
}

.global-report__metric-icon {
    width: 2.85rem;
    height: 2.85rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--metric-accent, var(--brand-primary, #f97316));
    background: rgba(249, 115, 22, 0.12);
    flex-shrink: 0;
}

.global-report__metric-copy {
    min-width: 0;
}

.global-report__metric-copy span {
    display: block;
    color: var(--secondary, #64748b);
    font-size: 0.78rem;
    line-height: 1.3;
}

.global-report__metric-copy strong {
    display: block;
    margin-top: 0.25rem;
    font-size: 1.1rem;
    color: var(--default-font-color, #0f172a);
    line-height: 1.25;
    word-break: break-word;
}

.global-report__panel {
    padding: 1rem 1.05rem;
    min-height: 100%;
}

.global-report__panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.9rem;
}

.global-report__panel-head h6 {
    margin: 0.15rem 0 0;
    font-size: 1rem;
    color: var(--default-font-color, #0f172a);
}

.global-report__panel-total {
    font-size: 1.1rem;
    color: var(--default-font-color, #0f172a);
}

.global-report__eyebrow {
    display: inline-block;
    color: var(--brand-primary, #f97316);
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
}

.global-report__chart-stage {
    position: relative;
    min-height: 16rem;
}

.global-report__briefing {
    display: grid;
    gap: 1rem;
}

.global-report__briefing-block > span {
    display: inline-block;
    margin-bottom: 0.75rem;
    font-size: 0.76rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--secondary, #64748b);
}

.global-report__leaderboard {
    display: grid;
    gap: 0.8rem;
}

.global-report__leaderboard--compact {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--default-border-color, #dbe4f0);
}

.global-report__leader {
    padding: 0.8rem 0.85rem;
    border: 1px solid var(--default-border-color, #dbe4f0);
    background: linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(255, 255, 255, 0.95));
}

.global-report__leader-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.8rem;
}

.global-report__leader-top strong,
.global-report__leader-top span {
    color: var(--default-font-color, #0f172a);
}

.global-report__leader-top strong {
    font-size: 0.95rem;
    line-height: 1.45;
}

.global-report__leader-top span {
    font-size: 0.82rem;
    font-weight: 700;
    white-space: nowrap;
}

.global-report__leader small {
    display: block;
    margin-top: 0.28rem;
    color: var(--secondary, #64748b);
    line-height: 1.45;
}

.global-report__leader-meter {
    margin-top: 0.65rem;
    height: 0.38rem;
    border-radius: 999px;
    background: rgba(148, 163, 184, 0.15);
    overflow: hidden;
}

.global-report__leader-meter span {
    display: block;
    height: 100%;
    border-radius: inherit;
}

.global-report__state,
.global-report__empty {
    border: 1px solid var(--default-border-color, #dbe4f0);
    background: var(--default-bg, #fff);
    color: var(--secondary, #64748b);
    text-align: center;
}

.global-report__state {
    padding: 3rem 1rem;
}

.global-report__empty {
    padding: 1.15rem 1rem;
}

.global-report__empty--full {
    padding: 2.5rem 1rem;
}

.global-report__select-menu.dropdown-menu {
    z-index: 1250 !important;
    width: 100%;
    min-width: 100%;
}

.global-report .date-picker-input,
.global-report .date-picker-input .input-group,
.global-report .search-filter-dropdown,
.global-report .search-filter-dropdown .dropdown {
    position: relative;
}

.global-report .search-filter-dropdown .dropdown,
.global-report .search-filter-dropdown .dropdown.show {
    position: relative;
    z-index: 40;
}

.global-report .search-filter-dropdown .dropdown.show {
    z-index: 1200;
}

.global-report .search-filter-dropdown .dropdown.show .dropdown-menu {
    z-index: 1250 !important;
}

.global-report__filters .vc-popover-content-wrapper,
.global-report__filters .vc-popover-content,
.global-report__filters .vc-container {
    z-index: 1260 !important;
}

@media (max-width: 1199.98px) {
    .global-report__filters-header {
        grid-template-columns: 1fr;
    }

    .global-report__filters-period {
        min-width: 0;
        text-align: left;
    }

    .global-report__filters-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .global-report__hero {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 767.98px) {
    .global-report__filters-grid {
        grid-template-columns: repeat(1, minmax(0, 1fr));
    }

    .global-report__filter-actions {
        flex-wrap: wrap;
        justify-content: flex-start;
    }

    .global-report__hero-grid {
        grid-template-columns: 1fr;
    }

    .global-report__score-shell {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>
